import React from 'react';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { LineWrapper } from '../styled';
import { TextLabel } from 'ds/components/typography';
import { TransportationSectionType } from 'store/state/insightsContext';
import AccessibleIcon from 'assets/svg/disabled.svg';
import { TransportationAccordionLine, TransportationLineShape } from 'store/state/selectors/insights/transportation';
import { SpacingBlock } from 'components/spacing-block';

interface LinesProps {
  lines: TransportationAccordionLine[];
  sectionId: TransportationSectionType;
  accessible: boolean;
}

const getVisibleLinesLimits = (isMobile: boolean) => {
  if (isMobile) {
    return {
      [TransportationSectionType.Train]: 10,
      [TransportationSectionType.Bus]: 10,
      [TransportationSectionType.Bike]: 10,
      [TransportationSectionType.Car]: 10,
      [TransportationSectionType.Transit]: 0,
    };
  }

  return {
    [TransportationSectionType.Train]: 12,
    [TransportationSectionType.Bus]: 8,
    [TransportationSectionType.Bike]: 12,
    [TransportationSectionType.Car]: 12,
    [TransportationSectionType.Transit]: 0,
  };
};

const getLinesCount = (linesCount: number, threshold: number, accessible: boolean) => {
  const offset = accessible ? 2 : 1;
  const moreThanNeed = linesCount > threshold - offset;
  return {
    linesToRender: moreThanNeed ? threshold - offset : linesCount,
    hiddenLines: moreThanNeed ? linesCount - threshold + offset : 0,
  };
};

const getExclusiveNameFormatting = (line: TransportationAccordionLine) => {
  switch (line.label) {
    case 'Hudson-Bergen Light Rail':
      return 'HBLR';
    default:
      return null;
  }
};

const getExclusiveLineShape = (line: TransportationAccordionLine): TransportationLineShape => {
  switch (line.label) {
    case 'Hudson-Bergen Light Rail':
      return 'square';
    default:
      return null;
  }
};

export const getLineShape = (type: TransportationSectionType, line: TransportationAccordionLine): TransportationLineShape => {
  let shape = getExclusiveLineShape(line);

  if (shape) return shape;

  switch (type) {
    case 'bus':
      shape = 'square';
      break;
    default:
      shape = 'circle';
  }

  return shape;
};

export const renderLineLabel = (line: TransportationAccordionLine) => {
  return getExclusiveNameFormatting(line) || line.label;
};

export const Lines: React.FC<LinesProps> = ({ lines, sectionId, accessible }) => {
  const range = useScreenBreakpoint();
  const isMobile = range === 1;

  const threshold = getVisibleLinesLimits(isMobile)[sectionId];
  const { linesToRender, hiddenLines } = getLinesCount(lines.length, threshold, accessible);

  const maybeAccessibleIcon = accessible ? <AccessibleIcon width={16} height={16} /> : null;
  const maybeHiddenLines = hiddenLines ? (
    <SpacingBlock mLeft={0.5} mRight={0.5}>
      <TextLabel>+{hiddenLines}</TextLabel>
    </SpacingBlock>
  ) : null;

  return (
    <>
      {maybeAccessibleIcon}
      {lines.slice(0, linesToRender).map((line, index) => (
        <LineWrapper
          key={`${line.label}${index}`}
          color={line.color}
          title={line.label}
          shape={getLineShape(sectionId, line)}
        >
          <TextLabel caps small>
            {renderLineLabel(line)}
          </TextLabel>
        </LineWrapper>
      ))}
      {maybeHiddenLines}
    </>
  );
};
